import type { RequestedTerms } from '@hts/shared';

export interface License {
  type: string;
  number: string;
  state: string;
  expiresAt: string;
}

export type CoiParserStatus = 'ok' | 'partial' | 'unreadable' | 'failed';

export interface FormData {
  // Step 1: Company Info
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  website: string;
  einTaxId: string;

  // Step 2: Contact
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  contactTitle: string;

  // Step 2: Authorized signer (optional — when someone else signs the agreement)
  hasAlternativeSigner: boolean;
  signerFirstName: string;
  signerLastName: string;
  signerEmail: string;
  signerTitle: string;

  // Step 3: Skills & Coverage
  skills: string[];
  serviceRadiusMi: number;
  isNationalBroker: boolean;

  // Step 4: Licenses
  licenses: License[];

  // Step 5: Insurance (COI)
  glExpiresAt: string;
  autoExpiresAt: string;
  wcExpiresAt: string;
  umbrellaExpiresAt: string;
  coiFile: File | null;
  coiAnalysisToken: string;
  coiParserStatus: CoiParserStatus | '';
  coiLimits: Record<string, string>;
  coiConfirmed: boolean;

  // Step 6: Agreement Terms
  // MUL-2333: `null` for `paymentTermsDays` is paid-when-paid (HTS default).
  // The form starts in that state; Net 30 / Net 45 / Net 60 are situational overrides.
  requestedTerms: RequestedTerms;
  // MUL-3919: required when the sub picks Net 30, Net 45 or Net 60. Server re-validates.
  requestReason: string;

  // Step 7: W-9
  w9File: File | null;

  // Step 8: ACH/Banking
  paymentMethod: 'ach' | 'check';
  achVendorName: string;
  achAddressLine1: string;
  achAddressLine2: string;
  achCity: string;
  achState: string;
  achZip: string;
  achBankName: string;
  achRoutingNumber: string;
  achAccountNumber: string;
  achAccountType: 'checking' | 'savings';
  bankVerifyFile: File | null;
}

export const INITIAL_FORM_DATA: FormData = {
  companyName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  website: '',
  einTaxId: '',
  contactFirstName: '',
  contactLastName: '',
  contactEmail: '',
  contactPhone: '',
  contactTitle: '',
  hasAlternativeSigner: false,
  signerFirstName: '',
  signerLastName: '',
  signerEmail: '',
  signerTitle: '',
  skills: [],
  serviceRadiusMi: 50,
  isNationalBroker: false,
  licenses: [],
  glExpiresAt: '',
  autoExpiresAt: '',
  wcExpiresAt: '',
  umbrellaExpiresAt: '',
  coiFile: null,
  coiAnalysisToken: '',
  coiParserStatus: '',
  coiLimits: {},
  coiConfirmed: false,
  requestedTerms: { paymentTermsDays: null },
  requestReason: '',
  w9File: null,
  paymentMethod: 'ach',
  achVendorName: '',
  achAddressLine1: '',
  achAddressLine2: '',
  achCity: '',
  achState: '',
  achZip: '',
  achBankName: '',
  achRoutingNumber: '',
  achAccountNumber: '',
  achAccountType: 'checking',
  bankVerifyFile: null,
};

// Eight steps by default. MUL-3919: when the verified email resolves to a
// company whose eligibility is false, the wizard skips "Agreement Terms"
// entirely — the API will not negotiate, and there is no choice for the
// sub to make. The form passes `paymentTermsNegotiable` to `OnboardingForm`,
// which derives `ACTIVE_STEP_TITLES` from this base list.
export const STEP_TITLES = [
  'Company Information',
  'Primary Contact',
  'Skills & Coverage',
  'Licenses',
  'Insurance (COI)',
  'Agreement Terms',
  'W-9 Form',
  'ACH / Banking',
  'Review & Submit',
] as const;

/**
 * Build the wizard's active step titles. When `paymentTermsNegotiable` is
 * false, "Agreement Terms" is hidden — eight steps, not nine.
 *
 * The index passed to `<OnboardingForm>` is now the *active* step index,
 * not the original. This keeps `ReviewStep` indexes consistent with the
 * `onEditStep` mapping the user expects: clicking "Edit" next to
 * Agreement Terms in review goes to its active index (or skips the
 * affordance entirely when the step is hidden).
 */
export function activeStepTitles(
  paymentTermsNegotiable: boolean,
): readonly string[] {
  if (paymentTermsNegotiable) return STEP_TITLES;
  return STEP_TITLES.filter((t) => t !== 'Agreement Terms');
}

/**
 * Map the logical step (one of the canonical nine) to its active index
 * under the current stepper. Used by ReviewStep's "Edit" buttons.
 */
export function stepEditIndex(
  paymentTermsNegotiable: boolean,
  canonicalStepIndex: number,
): number {
  if (paymentTermsNegotiable) return canonicalStepIndex;
  // The "Agreement Terms" canonical index is 5 (Insurance=4, then Terms).
  // When the terms step is hidden, every canonical index >= 5 shifts down by 1.
  if (canonicalStepIndex <= 4) return canonicalStepIndex;
  if (canonicalStepIndex === 5) {
    // Should never happen when the step is hidden — but stay safe.
    return canonicalStepIndex;
  }
  return canonicalStepIndex - 1;
}